import type { JSX, Dispatch, SetStateAction } from "react";
import { useState } from "react";
import { AutocompleteWithItems } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";

import { SearchContainer, SearchContainerWithBorder, InviteTitle } from "./InviteTeammatesList.styled";
import { getInviteTeammatesListItems, USERS_TO_DISPLAY } from "./helpers";
import type { IUserInfoExtended } from "app/components/SharingModal/types";
import { InviteTeammatesChipItem } from "app/components/SharingModal/components/InviteTeammatesChipItem/InviteTeammatesChipItem";
import { translate } from "app/filters/i18nFilter";

interface IInviteUsersListProps<T> {
    invitedUsers: T[];
    setInvitedUsers: Dispatch<SetStateAction<T[]>>;
    users: T[];
    inviteUsersPlaceholder: string;
    inviteTitle?: string;
}

export const InviteUsersList = <T extends IUserInfoExtended>({
    invitedUsers,
    setInvitedUsers,
    users,
    inviteUsersPlaceholder,
    inviteTitle = translate("global.sharingModal.inviteTitle"),
}: IInviteUsersListProps<T>): JSX.Element => {
    const [isFocused, setIsFocused] = useState<boolean>(false);

    const getData = async (query: string): Promise<JSX.Element[]> => {
        const foundUsers = users
            .filter(
                (user) =>
                    user.text.toLowerCase().includes(query.toLowerCase()) ||
                    user.email.toLowerCase().includes(query.toLowerCase()),
            )
            .slice(0, USERS_TO_DISPLAY);
        const resultedUsers = getInviteTeammatesListItems(foundUsers, handleSelectUser);

        return resultedUsers;
    };

    const handleSelectUser = (item: T) => async (): Promise<void> => {
        setIsFocused(false);

        const isUserAlreadySelected = invitedUsers.some((user) => user.id === item.id);
        if (isUserAlreadySelected) {
            return;
        }

        setInvitedUsers([...invitedUsers, item]);
    };

    const handleRemoveUser = (item: T) => (): void => {
        const updatedUsers = invitedUsers.filter((user) => user.id !== item.id);
        setInvitedUsers(updatedUsers);
    };

    const getChips = (): JSX.Element[] =>
        invitedUsers.map((user) => {
            return (
                <InviteTeammatesChipItem
                    key={user.id}
                    text={user.text}
                    isError={false}
                    onClick={handleRemoveUser(user)}
                />
            );
        });

    const handleFocus = (): void => {
        setIsFocused(true);
    };

    const handleBlur = (): void => {
        setIsFocused(false);
    };

    const inputHasBorder = invitedUsers.length > 0 && !isFocused;

    return (
        <div>
            <InviteTitle>{inviteTitle}</InviteTitle>
            <SearchContainerWithBorder hasBorder={inputHasBorder}>
                <SearchContainer isDisabled={false}>
                    <AutocompleteWithItems
                        selectedItems={getChips()}
                        getListItems={getData}
                        placeholder={inviteUsersPlaceholder}
                        searchIcon="share-new"
                        loadingComponent={<DotsLoader />}
                        disabled={false}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        isFocused={isFocused}
                    />
                </SearchContainer>
            </SearchContainerWithBorder>
        </div>
    );
};
